import { shownWeight, shownWeightUnit, showWeight } from "./units";
// @ts-check
// Shared visual helpers for workout plan display components.
// Used by SessionPlanOutline, CoachView, and WorkoutCoachView.

/** @typedef {import("../types/plan").PlanNode} PlanNode */

// Recovery nodes whose label begins with "Transition" or "Setup" (Rule 7 of
// docs/llm_system_prompt.md). Prefix-match so descriptive variants like
// "Transition to Metcon" or "Setup for Squats" also resolve — LLMs naturally
// produce these.
export const isTransitionLabel = (label = "") => {
  const safe = label ? String(label).trim().toLowerCase() : "";
  return safe.startsWith("transition") || safe.startsWith("setup");
};

// Sub-check for "Setup" labels specifically. The transition injector uses this
// label only for inter-block boundaries.
export const isSetupLabel = (label = "") => {
  const safe = label ? String(label).trim().toLowerCase() : "";
  return safe.startsWith("setup");
};

// Issue #22 draws a hard line between two kinds of injected changeover node:
//
//   • A "Setup section" (label "Setup…") — dedicated time to set up new
//     equipment between blocks (e.g. rack a barbell). These STAY visible: a
//     chapter-break in the plan, a real setup phase in the coach.
//
//   • A "transition" (anything else type:Transition / "Transition…"-labelled) —
//     just moving between movements (e.g. bench → squats). These are time-only:
//     hidden from every plan/summary view, stripped from shared/saved sessions,
//     and skipped by the engine at runtime (the compiler tags them
//     `executable:false`). Their duration still counts toward the projected
//     total so estimates stay realistic.

// True for a Setup section (kept visible / executable).
/** @param {PlanNode} node */
export const isSetupNode = (node) => {
  if (!node) return false;
  const type = (node.type || "").toLowerCase();
  if (type !== "transition" && type !== "rest" && type !== "setrest") {
    return false;
  }
  return isSetupLabel(node.label);
};

// True for a time-only transition (hidden everywhere; never a Setup section).
/** @param {PlanNode} node */
export const isTransitionNode = (node) => {
  if (!node) return false;
  const type = (node.type || "").toLowerCase();
  const transitionTyped =
    type === "transition" ||
    ((type === "rest" || type === "setrest") && isTransitionLabel(node.label));
  return transitionTyped && !isSetupLabel(node.label);
};

// Any parser-injected changeover node — a time-only transition OR a Setup
// section. transitionInjector inserts both (the LLM never authors them), so
// neither belongs in a shared/saved session: they are re-derived when the plan
// is next compiled. Issue #22.
/** @param {PlanNode} node */
export const isInjectedChangeover = (node) =>
  isTransitionNode(node) || isSetupNode(node);

// Should this node be hidden from a plan/summary list? True for:
//   • Any time-only transition (see isTransitionNode) — Setup sections stay.
//   • Any `Rest`/`Transition` with `value: 0` — the zero-rest superset escape
//     hatch (see transitionInjector.js); a no-op marker that would be visual
//     noise as a row.
// The compiled queue and total-time math still include these nodes; only the
// visual lists elide them.
/** @param {PlanNode} node */
export const isHiddenInSummary = (node) => {
  if (!node) return false;
  const type = (node.type || "").toLowerCase();
  if ((type === "rest" || type === "transition") && Number(node.value) === 0) {
    return true; // zero-rest superset marker
  }
  return isTransitionNode(node);
};

// Structural detection: a Rest is a "round-boundary rest" when it sits at the
// end of a multi-round Group's children. Callers (e.g. the divider renderer)
// can use this to apply chapter-break styling without relying on a separate
// SetRest type. `parent` is the immediate parent node; `index` is the rest's
// position in `parent.children`.
export const isRoundBoundaryRest = (node, parent, index) => {
  if (!node || !parent) return false;
  if ((node.type || "").toLowerCase() !== "rest") return false;
  if (parent.type !== "Group") return false;
  const rounds = Number(parent.value);
  if (!Number.isFinite(rounds) || rounds <= 1) return false;
  if (!Array.isArray(parent.children)) return false;
  return index === parent.children.length - 1;
};

// EMOM / E2MOM / E3MOM… — a fixed-WINDOW protocol: the work starts at the top
// of the window and the last child is the residual rest that completes it. That
// rest is prescribed by the format, not incidental, so the passes that normally
// discard a short trailing/mid-sequence rest (the compiler's round-boundary
// strip, the normaliser's ghost/micro-transition demotion) must spare it —
// otherwise a "12-Min EMOM" runs short of its own label (issue #63).
export const PROTOCOL_WINDOW_LABEL_RE = /\be\d*mom\b/i;

/** @param {PlanNode} node */
export const isProtocolWindowGroup = (node) =>
  node?.type === "Group" &&
  PROTOCOL_WINDOW_LABEL_RE.test(String(node.label || ""));

// Exercise-category classification used by the icon picker. Keyword-based so
// new exercises slot in without needing a canonical-key entry in
// repEstimator. Returns one of: "barbell", "bodyweight", "cardio_machine",
// "power", "core", or null (renderer falls back to default).
const CATEGORY_KEYWORDS = [
  // Order matters — more specific keywords first.
  { category: "cardio_machine", keywords: ["rower", "row erg", "concept 2", "concept2", "c2", "assault bike", "echo bike", "echobike", "airdyne", "bike erg", "ski erg", "skierg", "treadmill", "stair", "stairmaster", "elliptical", "spin"] },
  { category: "power", keywords: ["slam", "snatch", "clean", "jerk", "thruster", "burpee", "box jump", "broad jump", "vertical jump", "plyo", "sprint", "kettlebell swing", "kb swing"] },
  { category: "barbell", keywords: ["squat", "deadlift", "bench", "press", "row", "ohp", "overhead", "front rack", "rdl", "good morning", "barbell"] },
  { category: "bodyweight", keywords: ["push-up", "pushup", "push up", "pull-up", "pullup", "pull up", "chin-up", "chinup", "dip", "sit-up", "situp", "sit up", "crunch", "plank", "hollow", "lunge", "step-up", "stepup", "step up", "muscle-up", "muscle up", "handstand"] },
  { category: "core", keywords: ["plank", "hollow hold", "ab", "core", "russian twist"] },
];

export const categoriseExercise = (label = "") => {
  const safe = String(label || "").toLowerCase();
  if (!safe) return null;
  for (const { category, keywords } of CATEGORY_KEYWORDS) {
    if (keywords.some((kw) => safe.includes(kw))) return category;
  }
  return null;
};

// --- Type badge colours (for type labels like [WORK], [REST]) ---

export const typeBadgeColor = {
  work: "bg-work/20 text-work",
  warmup: "bg-warmup/20 text-warmup",
  cooldown: "bg-cooldown/20 text-cooldown",
  rest: "bg-rest/20 text-rest",
  transition: "bg-transition/20 text-transition",
};

export const getBadgeClass = (type, label = "") => {
  const key = (type || "").toLowerCase().split(" ")[0];
  if (key === "transition") return typeBadgeColor.transition;
  if (isTransitionLabel(label)) return typeBadgeColor.transition;
  return typeBadgeColor[key] || "bg-muted text-muted-foreground";
};

// --- Type dot colours (for small indicator dots) ---

export const typeDotColor = {
  work: "bg-work",
  warmup: "bg-warmup",
  cooldown: "bg-cooldown",
  rest: "bg-rest",
  transition: "bg-transition",
};

export const getDotClass = (type, label = "") => {
  const key = (type || "").toLowerCase().split(" ")[0];
  if (key === "transition") return typeDotColor.transition;
  if (isTransitionLabel(label)) return typeDotColor.transition;
  return typeDotColor[key] || "bg-muted-foreground";
};

// --- Value formatting ---

export const formatExVal = (value, unit) => {
  if (!value) return "";
  if (unit === "reps") return `${value} reps`;
  if (unit === "cal") return `${value} cal`;
  if (unit === "s") return `${value}s`;
  if (unit === "m") return `${value}m`;
  return `${value}`;
};

// The schema has no distance unit, so a distance target is stored as its
// estimated seconds plus `estimatedFromDistanceM` provenance (see
// reinterpretErgDistance / the EMOM station converter in postprocess.js). The
// PRESCRIPTION is the distance — "200 m", not the ~48s it is expected to take —
// so anything rendering a node's value should prefer it. Falls back to
// formatExVal for every other node.
/** @param {PlanNode} node */
export const formatNodeVal = (node) => {
  const metres = Number(node?.estimatedFromDistanceM);
  if (Number.isFinite(metres) && metres > 0) return `${Math.round(metres)} m`;
  return formatExVal(node?.value, node?.unit);
};

// --- Per-set variation (`setScheme`, issue #29) ---

// Numeric per-set dimensions a `setScheme` may vary. Each maps to a field on the
// Work node; a constant parameter is never arrayed. Display order.
export const SET_SCHEME_DIMENSIONS = ["value", "weight", "intensityPct"];

// Suffix for each varying setScheme dimension. `value` takes the node's own
// unit (reps / s / m); load dimensions carry their own units.
const SET_SCHEME_DIM_SUFFIX = { weight: "kg", intensityPct: "% 1RM" };

// Resolve a Work node's parameters for a specific set index when it carries a
// `setScheme`. Returns the node unchanged (preserving reference equality) when
// there is nothing to vary, otherwise a shallow clone with the per-set values
// applied. Graceful degradation: a missing or out-of-range array entry leaves
// the static field in place, so a length mismatch can never throw — and set
// COUNT is governed solely by the enclosing Group's `value`, never the array
// length, so a bad scheme can never multiply or drop sets (the #29 bug class).
//
// Shared by workoutCompiler (which bakes the result onto the execution queue)
// and the plan/outline renderers (issue #42), so the figures you read and the
// ones you run are resolved by the same function.
/** @param {PlanNode} node */
export const resolveSetScheme = (node, setIdx) => {
  const scheme = node?.setScheme;
  if (!scheme || typeof scheme !== "object") return node;
  let clone = null;
  for (const dim of SET_SCHEME_DIMENSIONS) {
    const arr = scheme[dim];
    if (!Array.isArray(arr)) continue;
    const v = arr[setIdx];
    if (!Number.isFinite(v)) continue; // fall back to the static field
    if (!clone) clone = { ...node };
    clone[dim] = v;
  }
  return clone || node;
};

// Structured view of a node's per-set scheme: one entry per VARYING dimension,
// in display order, as `{ dim, values, suffix }`. Renderers that style
// individual entries (the outline lights the active set) build from this; the
// formatters below flatten it to a string.
/** @param {PlanNode} node */
export const buildSetSchemeSegments = (node) => {
  const scheme = node?.setScheme;
  if (!scheme || typeof scheme !== "object") return [];
  const segments = [];
  for (const dim of SET_SCHEME_DIMENSIONS) {
    const arr = scheme[dim];
    if (!Array.isArray(arr) || arr.length === 0) continue;
    // A load dimension that is zero for every set is the ABSENCE of a load,
    // not a load of zero: bodyweight work carries weight [0,0,0] from the
    // editors. formatNodeLoad has always skipped that for a single figure —
    // the per-set path printing "0/0/0 kg · 0/0/0 % 1RM" was the same rule
    // going unapplied, and worse, it displaced the RPE cue formatLoadSummary
    // falls back to when there is no load. A ramp that merely STARTS at zero
    // (0/60/70 — bodyweight, then loaded) is a real prescription and stays.
    if (dim !== "value" && !arr.some((v) => Number.isFinite(v) && v > 0)) {
      continue;
    }
    // Weights show in the Profile unit (#191); the plan keeps kg.
    if (dim === "weight") {
      segments.push({ dim, values: arr.map((v) => (Number.isFinite(v) ? shownWeight(v) : v)), suffix: shownWeightUnit() });
      continue;
    }
    const suffix = dim === "value" ? node.unit : SET_SCHEME_DIM_SUFFIX[dim];
    segments.push({ dim, values: arr, suffix: suffix || "" });
  }
  return segments;
};

const formatSegment = ({ values, suffix }) =>
  suffix ? `${values.join("/")} ${suffix}` : values.join("/");

const formatSegments = (segments) => segments.map(formatSegment).join(" · ");

// Compact one-line summary of a Work node's per-set scheme, e.g.
// "8/6/5/5 reps · 60/65/70/70 kg". Returns "" when the node carries no scheme.
export const formatSetScheme = (node) =>
  formatSegments(buildSetSchemeSegments(node));

// Just the `value` dimension — the per-set reps/seconds ramp, e.g.
// "8/7/6/5 reps". This is the prescription a plan view shows in place of the
// static field, which by schema rule only mirrors set 1 and so reads as "8 reps
// every set" (issue #42).
export const formatSetSchemeValues = (node) =>
  formatSegments(
    buildSetSchemeSegments(node).filter((seg) => seg.dim === "value"),
  );

// Just the load dimensions — e.g. "60/65/70/70 kg". Rendered on its own line so
// the dense value column stays a single quantity.
export const formatSetSchemeLoads = (node) =>
  formatSegments(
    buildSetSchemeSegments(node).filter((seg) => seg.dim !== "value"),
  );

// The load prescribed for ONE node, e.g. "90 kg · 75% 1RM". Used by the Coach,
// which runs the already per-set-resolved node, so this reads the set actually
// in progress rather than the static set-1 mirror (issue #74). Returns "" when
// the node carries no load at all (bodyweight work, rests).
/** @param {PlanNode} node */
export const formatNodeLoad = (node) => {
  if (!node) return "";
  const parts = [];
  for (const dim of ["weight", "intensityPct"]) {
    const v = Number(node[dim]);
    if (!Number.isFinite(v) || v <= 0) continue;
    parts.push(dim === "weight" ? showWeight(v) : `${v} ${SET_SCHEME_DIM_SUFFIX[dim]}`.replace(" %", "%"));
  }
  return parts.join(" · ");
};

// The whole load story for ONE authored node, in the form a recap wants: the
// per-set ramp when the node varies its load, the single figure when it does
// not. Falls back to the authored effort cue (`intensityTarget` — "RPE 8",
// "2k pace") only when there is no numeric load, since a cue printed beside the
// weight it already describes is noise, not information.
/** @param {PlanNode} node */
export const formatLoadSummary = (node) => {
  if (!node) return "";
  const ramp = formatSetSchemeLoads(node);
  if (ramp) return ramp;
  const single = formatNodeLoad(node);
  if (single) return single;
  const cue = typeof node.intensityTarget === "string" ? node.intensityTarget.trim() : "";
  return cue;
};

// Long-form enumeration for tooltips: "Set 1: 8 reps, 60 kg · Set 2: 7 reps,
// 65 kg · …". Set count follows the longest arrayed dimension; a dimension with
// no entry for a set is simply omitted from that set's part.
/** @param {PlanNode} node */
export const describeSetScheme = (node) => {
  const segments = buildSetSchemeSegments(node);
  if (segments.length === 0) return "";
  const setCount = Math.max(...segments.map((seg) => seg.values.length));
  const sets = [];
  for (let i = 0; i < setCount; i++) {
    const parts = segments
      .filter((seg) => Number.isFinite(seg.values[i]))
      .map((seg) => formatSegment({ values: [seg.values[i]], suffix: seg.suffix }));
    if (parts.length > 0) sets.push(`Set ${i + 1}: ${parts.join(", ")}`);
  }
  return sets.join(" · ");
};

// segmentPulseMs drives the active ring segment's colour-to-white pulse. Kept
// at 2 s or slower: a full white flash every second reads as a strobe over a
// long step. Work stays quicker than rest so the two phases still feel
// different.
const COACH_MOTION_TOKENS = {
  work: {
    progressTransitionMs: 900,
    segmentPulseMs: 2000,
    indeterminatePulseMs: 1000,
    dotPulseMs: 1000,
    dotTransitionMs: 300,
    dotPulseClass: "animate-pulse-fade",
  },
  rest: {
    progressTransitionMs: 900,
    segmentPulseMs: 2800,
    indeterminatePulseMs: 2000,
    dotPulseMs: 2000,
    dotTransitionMs: 300,
    dotPulseClass: "animate-pulse-fade",
  },
  warmup: {
    progressTransitionMs: 900,
    segmentPulseMs: 2200,
    indeterminatePulseMs: 1200,
    dotPulseMs: 1200,
    dotTransitionMs: 300,
    dotPulseClass: "animate-pulse-fade",
  },
  cooldown: {
    progressTransitionMs: 900,
    segmentPulseMs: 2600,
    indeterminatePulseMs: 1600,
    dotPulseMs: 1600,
    dotTransitionMs: 300,
    dotPulseClass: "animate-pulse-fade",
  },
  countin: {
    progressTransitionMs: 700,
    segmentPulseMs: 700,
    indeterminatePulseMs: 700,
    dotPulseMs: 700,
    dotTransitionMs: 250,
    dotPulseClass: "animate-pulse-fade",
  },
  transition: {
    progressTransitionMs: 700,
    segmentPulseMs: 1400,
    indeterminatePulseMs: 1400,
    dotPulseMs: 1400,
    dotTransitionMs: 250,
    dotPulseClass: "animate-pulse-fade",
  },
};

export const getPhaseVisualType = (phaseType, label = "") => {
  const typeKey = (phaseType || "").toLowerCase().replace(/\s+/g, "");

  if (typeKey === "transition") return "transition";
  if (isTransitionLabel(label)) return "transition";

  if (typeKey.includes("count")) return "countin";
  if (typeKey.includes("rest")) return "rest";
  if (typeKey.includes("warmup")) return "warmup";
  if (typeKey.includes("cooldown")) return "cooldown";

  return "work";
};

export const getPhaseMotionTokens = (phaseType, label = "") => {
  const typeKey = getPhaseVisualType(phaseType, label);
  return COACH_MOTION_TOKENS[typeKey] || COACH_MOTION_TOKENS.work;
};

// --- Active state colours (for highlighting the current phase) ---

// Map phase types to semantic active states using custom theme tokens.
export const getActiveTokens = (phaseType, label = "") => {
  const typeKey = getPhaseVisualType(phaseType, label);

  if (typeKey === "transition") {
    return {
      bg: "bg-transition/15",
      border: "border-transition/50",
      text: "text-transition",
      time: "text-transition font-bold",
      glow: "drop-shadow-[0_0_6px_rgba(115,125,140,0.4)]",
    };
  }
  if (typeKey === "rest") {
    return {
      bg: "bg-rest/15",
      border: "border-rest/50",
      text: "text-rest",
      time: "text-rest font-bold",
      glow: "drop-shadow-[0_0_8px_rgba(59,130,246,0.5)]", // Replace rgba with your actual rest hex if needed
    };
  }
  if (typeKey === "warmup") {
    return {
      bg: "bg-warmup/15",
      border: "border-warmup/50",
      text: "text-warmup",
      time: "text-warmup font-bold",
      glow: "drop-shadow-[0_0_8px_rgba(245,158,11,0.5)]", // Replace rgba with your actual warmup hex
    };
  }
  if (typeKey === "cooldown") {
    return {
      bg: "bg-cooldown/15",
      border: "border-cooldown/50",
      text: "text-cooldown",
      time: "text-cooldown font-bold",
      glow: "drop-shadow-[0_0_8px_rgba(20,184,166,0.5)]", // Replace rgba with your actual cooldown hex
    };
  }

  // Default to Work / High Intensity
  // Assumes primary and work are mathematically identical or close in your theme
  return {
    bg: "bg-primary/15",
    border: "border-primary/50",
    text: "text-primary",
    time: "text-primary font-bold",
    glow: "drop-shadow-glow-work",
  };
};

// --- Generation validation provenance ---
//
// generateWorkoutPlan stamps whatever the deterministic post-processing and the
// single refinement pass could NOT fix onto `meta.validation.issues`
// (validatePlan). Nothing read it, so a plan whose refinement failed to clear a
// blocker was returned and rendered looking perfectly ordinary — which is how a
// 30-minute request could arrive as a 90-second session with no indication at
// all. Soft issues stay silent: they are provenance for telemetry, not
// something the user has to act on.
//
// Lives here rather than in SessionPlanDisplay because that module transitively
// imports llmApi (ESM-only @google/genai), which Jest cannot transform.
export const blockingValidationIssues = (plan) => {
  const issues = plan?.meta?.validation?.issues;
  if (!Array.isArray(issues)) return [];
  return issues.filter((issue) => issue?.severity === "blocking");
};
